import { Component, computed, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxIconButtonComponent } from '@ui/icon-button/icon-button.component';
import { VxItemCellComponent } from '@ui/item-cell/item-cell.component';
import { VxListRowComponent } from '@ui/list-row/list-row.component';
import { VxPanelComponent } from '@ui/panel/panel.component';
import { VxSwitchComponent } from '@ui/switch/switch.component';
import { ConfirmService } from 'src/app/services/dialog/confirm.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { ListEntitiesToIterable } from 'src/app/store/common/list-entity';
import {
  DeleteNotificationScheduleAction,
  LoadNotificationSchedulesAction,
  UpdateNotificationScheduleAction,
} from 'src/app/store/notifications/notifications.actions';
import { NotificationsState } from 'src/app/store/notifications/notifications.state';
import { NotificationsStateModule } from 'src/app/store/notifications/notifications.state.module';
import { AuthHeaderComponent } from '../components/auth-header/auth-header.component';
import { AuthShellComponent } from '../components/auth-shell/auth-shell.component';

/**
 * The schedules a notification mail links to (`?jwt=…`, no sign-in): pause
 * or delete them right from the mail.
 */
@Component({
  selector: 'app-notification-preferences-page',
  templateUrl: './notification-preferences.component.html',
  imports: [
    FormsModule,
    RouterModule,
    TranslocoModule,
    NotificationsStateModule,
    AuthHeaderComponent,
    AuthShellComponent,
    VxButtonComponent,
    VxIconButtonComponent,
    VxItemCellComponent,
    VxListRowComponent,
    VxPanelComponent,
    VxSwitchComponent,
  ],
})
export class PublicNotificationPreferencesPageComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmService = inject(ConfirmService);
  private readonly translationService = inject(TranslationService);
  private readonly jwt =
    this.route.snapshot.queryParamMap.get('jwt') ?? undefined;

  private readonly scheduleEntities = this.store.selectSignal(
    NotificationsState.schedules
  );

  protected readonly scheduleEntitiesIterable = computed(() =>
    ListEntitiesToIterable(this.scheduleEntities()!)
  );

  protected readonly isEmpty = computed(
    () => this.scheduleEntitiesIterable().entities.length === 0
  );

  ngOnInit(): void {
    if (this.jwt) {
      this.store.dispatch(new LoadNotificationSchedulesAction(this.jwt));
    }
  }

  protected deleteSchedule(scheduleId: string): void {
    this.confirmService
      .confirm({
        title: 'resources.notificationSchedule.actions.delete.long',
        description:
          'resources.notificationSchedule.actions.delete.confirmation',
        confirmLabel: 'resources.notificationSchedule.actions.delete.short',
        tone: 'danger',
        icon: 'trash',
      })
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        this.store.dispatch(
          new DeleteNotificationScheduleAction(scheduleId, this.jwt)
        );
      });
  }

  protected setScheduleEnabled(scheduleId: string, enabled: boolean): void {
    this.store.dispatch(
      new UpdateNotificationScheduleAction(scheduleId, { enabled }, this.jwt)
    );
  }

  protected translateCronExpression(expression: string): string {
    return this.translationService.translateCronExpression(expression, {
      verbose: true,
    });
  }
}
